import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { formatDistanceToNow, differenceInMinutes } from 'date-fns';
import {
  ArrowLeft,
  Plus,
  Server,
  Wifi,
  WifiOff,
  Activity,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Clock,
  Trash2,
  Pause,
  Play,
} from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { supabaseHelpers } from '../lib/supabaseClient';
import ResponseTimeChart from '../components/ResponseTimeChart';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();

  const [service, setService] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [logs, setLogs] = useState({});
  const [uptimeData, setUptimeData] = useState({});
  const [incidents, setIncidents] = useState([]);
  const [selectedEndpointLogs, setSelectedEndpointLogs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchServiceData = useCallback(async () => {
    try {
      if (!mountedRef.current) return;
      setLoading(true);
      if (!mountedRef.current) return;
      setError(null);

      // Fetch service details
      const serviceData = await supabaseHelpers.services.getService(serviceId);
      if (!mountedRef.current) return;
      setService(serviceData);

      // Fetch endpoints for this service
      const endpointsData = await supabaseHelpers.endpoints.getEndpointsByService(
        serviceId
      );
      if (!mountedRef.current) return;
      setEndpoints(endpointsData);

      // Fetch latest logs and uptime for each endpoint in parallel
      const logsMap = {};
      const uptimeMap = {};

      const logsResults = await Promise.allSettled(
        endpointsData.map((endpoint) =>
          supabaseHelpers.monitorLogs.getEndpointLogs(endpoint.id, 1)
        )
      );

      const uptimeResults = await Promise.allSettled(
        endpointsData.map((endpoint) =>
          supabaseHelpers.monitorLogs.getEndpointUptime(endpoint.id, 24)
        )
      );

      endpointsData.forEach((endpoint, index) => {
        const logResult = logsResults[index];
        if (logResult.status === 'fulfilled' && logResult.value.length > 0) {
          logsMap[endpoint.id] = logResult.value[0];
        }

        const uptimeResult = uptimeResults[index];
        if (uptimeResult.status === 'fulfilled') {
          uptimeMap[endpoint.id] = uptimeResult.value.uptime !== null
            ? parseFloat(uptimeResult.value.uptime)
            : null;
        }
      });

      if (!mountedRef.current) return;
      setLogs(logsMap);
      if (!mountedRef.current) return;
      setUptimeData(uptimeMap);

      // Fetch incidents for all endpoints in this service in parallel
      const incidentResults = await Promise.allSettled(
        endpointsData.map((endpoint) =>
          supabaseHelpers.incidents.getEndpointIncidents(endpoint.id)
        )
      );

      const allIncidents = [];
      endpointsData.forEach((endpoint, index) => {
        const result = incidentResults[index];
        if (result.status === 'fulfilled') {
          result.value.forEach((incident) => {
            incident.endpoint = endpoint;
          });
          allIncidents.push(...result.value);
        }
      });

      // Sort by most recent first and take top 10
      allIncidents.sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
      if (!mountedRef.current) return;
      setIncidents(allIncidents.slice(0, 10));
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err.message);
    } finally {
      if (!mountedRef.current) return;
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => {
    if (currentOrganization) {
      fetchServiceData();
      const interval = setInterval(fetchServiceData, 30000);
      return () => clearInterval(interval);
    }
  }, [currentOrganization, fetchServiceData]);

  const handleAddEndpoint = () => {
    navigate(`/admin/add-endpoint?serviceId=${serviceId}`);
  };

  const handleToggleEndpoint = async (endpointId, currentStatus) => {
    try {
      await supabaseHelpers.endpoints.toggleEndpoint(endpointId, !currentStatus);
      await fetchServiceData();
    } catch (err) {
      alert('Failed to toggle endpoint. Please try again.');
    }
  };

  const handleDeleteEndpoint = async (endpointId, endpointLabel) => {
    if (!window.confirm(`Are you sure you want to delete "${endpointLabel}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await supabaseHelpers.endpoints.deleteEndpoint(endpointId);

      // Create audit log entry
      if (user && currentOrganization) {
        await supabaseHelpers.auditLogs.createAuditLog({
          organization_id: currentOrganization.id,
          user_id: user.id,
          action: 'delete_endpoint',
          resource_type: 'endpoint',
          resource_id: endpointId,
          details: { endpoint_label: endpointLabel, service_id: serviceId },
        });
      }

      await fetchServiceData();
    } catch (err) {
      alert('Failed to delete endpoint. Please try again.');
    }
  };

  const fetchEndpointDetailedLogs = async (endpointId) => {
    try {
      const logs = await supabaseHelpers.monitorLogs.getEndpointLogs(endpointId, 288);
      setSelectedEndpointLogs((prev) => ({ ...prev, [endpointId]: logs }));
    } catch (err) {
      alert('Failed to fetch response time data. Please try again.');
    }
  };

  const toggleEndpointChart = (endpointId) => {
    if (selectedEndpointLogs[endpointId]) {
      // Remove logs to hide chart
      setSelectedEndpointLogs((prev) => {
        const newLogs = { ...prev };
        delete newLogs[endpointId];
        return newLogs;
      });
    } else {
      // Fetch logs to show chart
      fetchEndpointDetailedLogs(endpointId);
    }
  };

  if (loading && !service) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003F5C] via-[#3D4752] to-[#5E4B8B] flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-8 h-8 text-[#2C9C7A] animate-spin mx-auto mb-4" />
          <p className="text-white">Loading service...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003F5C] via-[#3D4752] to-[#5E4B8B] p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
            <p className="text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003F5C] via-[#3D4752] to-[#5E4B8B] p-8">
        <div className="max-w-7xl mx-auto">
          <p className="text-white">Service not found</p>
        </div>
      </div>
    );
  }

  // Calculate service statistics
  const totalEndpoints = endpoints.length;
  let operationalCount = 0;
  let totalUptime = 0;
  let totalResponseTime = 0;
  let hasData = false;

  endpoints.forEach((endpoint) => {
    const log = logs[endpoint.id];
    if (log) {
      hasData = true;
      if (log.is_success) operationalCount++;
      const uptime = uptimeData[endpoint.id] || 100;
      totalUptime += uptime;
      if (log.response_time_ms) {
        totalResponseTime += log.response_time_ms;
      }
    }
  });

  const averageUptime = totalEndpoints > 0 && hasData ? totalUptime / totalEndpoints : 100;
  const averageResponseTime = totalEndpoints > 0 && hasData ? Math.round(totalResponseTime / totalEndpoints) : 0;
  const isServiceOperational = operationalCount === totalEndpoints;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#003F5C] via-[#3D4752] to-[#5E4B8B] p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-gray-300 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className="p-4 rounded-xl"
                style={{
                  backgroundColor: service.color
                    ? `${service.color}20`
                    : 'rgba(139, 92, 246, 0.2)',
                }}
              >
                <Server
                  className="w-10 h-10"
                  style={{ color: service.color || '#8B5CF6' }}
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">{service.name}</h1>
                {service.description && (
                  <p className="text-gray-300 mt-1">{service.description}</p>
                )}
              </div>
            </div>

            <button
              onClick={handleAddEndpoint}
              className="flex items-center gap-2 px-4 py-2 bg-[#2C9C7A] hover:bg-[#168980] text-white rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Endpoint
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Status */}
          <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              {isServiceOperational ? (
                <CheckCircle className="w-6 h-6 text-emerald-400" />
              ) : (
                <AlertCircle className="w-6 h-6 text-[#B0413E]" />
              )}
              <p className="text-gray-400 text-sm">Status</p>
            </div>
            <p className="text-2xl font-bold text-white">
              {isServiceOperational ? 'Operational' : 'Issues Detected'}
            </p>
          </div>

          {/* Endpoints */}
          <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Server className="w-6 h-6 text-[#3599B8]" />
              <p className="text-gray-400 text-sm">Endpoints</p>
            </div>
            <p className="text-2xl font-bold text-white">
              {operationalCount}/{totalEndpoints}
            </p>
          </div>

          {/* Uptime */}
          <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-6 h-6 text-emerald-400" />
              <p className="text-gray-400 text-sm">Uptime (24h)</p>
            </div>
            <p className="text-2xl font-bold text-white">{averageUptime.toFixed(2)}%</p>
          </div>

          {/* Avg Response Time */}
          <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-6 h-6 text-blue-400" />
              <p className="text-gray-400 text-sm">Avg Response</p>
            </div>
            <p className="text-2xl font-bold text-white">{averageResponseTime}ms</p>
          </div>
        </div>

        {/* Endpoints List */}
        <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6">
          <h2 className="text-xl font-bold text-white mb-4">Endpoints</h2>

          {endpoints.length === 0 ? (
            <div className="text-center py-12">
              <Server className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">No endpoints in this service yet</p>
              <button
                onClick={handleAddEndpoint}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C9C7A] hover:bg-[#168980] text-white rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add First Endpoint
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {endpoints.map((endpoint) => {
                const log = logs[endpoint.id];
                const isOperational = log?.is_success ?? false;
                const uptime = uptimeData[endpoint.id] || 100;

                return (
                  <div
                    key={endpoint.id}
                    className="p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      {/* Endpoint Info */}
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0 p-2 bg-white/5 rounded-xl">
                          {log ? (
                            isOperational ? (
                              <Wifi className="w-6 h-6 text-emerald-400" />
                            ) : (
                              <WifiOff className="w-6 h-6 text-[#B0413E]" />
                            )
                          ) : (
                            <Activity className="w-6 h-6 text-gray-400 animate-pulse" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">
                            {endpoint.label}
                          </h3>
                          <p className="text-sm text-gray-400">{endpoint.url}</p>
                        </div>
                      </div>

                      {/* Endpoint Stats */}
                      <div className="flex items-center space-x-6">
                        <div className="text-right">
                          <p className="text-xs text-gray-500 uppercase tracking-wider">
                            Response
                          </p>
                          <p className="text-lg font-semibold text-white">
                            {log ? `${log.response_time_ms}ms` : '--'}
                          </p>
                        </div>

                        <div className="text-right">
                          <p className="text-xs text-gray-500 uppercase tracking-wider">
                            Uptime
                          </p>
                          <p
                            className={`text-lg font-semibold ${
                              uptime >= 99
                                ? 'text-emerald-400'
                                : uptime >= 95
                                ? 'text-yellow-400'
                                : 'text-[#B0413E]'
                            }`}
                          >
                            {uptime.toFixed(1)}%
                          </p>
                        </div>

                        <div className="text-right">
                          <p className="text-xs text-gray-500 uppercase tracking-wider">
                            Status
                          </p>
                          <div
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                              isOperational
                                ? 'bg-emerald-500/10 text-emerald-400'
                                : 'bg-red-500/10 text-[#B0413E]'
                            }`}
                          >
                            {isOperational ? 'Operational' : 'Down'}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleToggleEndpoint(endpoint.id, endpoint.is_active)}
                            className={`p-2 rounded-lg transition-colors ${
                              endpoint.is_active
                                ? 'bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400'
                                : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400'
                            }`}
                            title={endpoint.is_active ? 'Pause monitoring' : 'Resume monitoring'}
                          >
                            {endpoint.is_active ? (
                              <Pause className="w-5 h-5" />
                            ) : (
                              <Play className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteEndpoint(endpoint.id, endpoint.label)}
                            className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                            title="Delete endpoint"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Error Display */}
                    {log && !isOperational && log.error_message && (
                      <div className="mt-4 p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                        <p className="text-sm text-red-300">
                          <span className="font-semibold">Error:</span> {log.error_message}
                        </p>
                        {log.error_type && (
                          <p className="text-sm text-red-400/80 mt-2">
                            <span className="font-medium">Type:</span> {log.error_type}
                          </p>
                        )}
                        {log.status_code && (
                          <p className="text-sm text-red-400/80 mt-1">
                            <span className="font-medium">Status Code:</span> {log.status_code}
                          </p>
                        )}
                        {(log.is_timeout || log.is_connection_error) && (
                          <div className="flex gap-2 mt-2">
                            {log.is_timeout && (
                              <span className="text-xs px-2 py-1 bg-orange-500/20 text-orange-300 rounded font-medium">
                                Timeout
                              </span>
                            )}
                            {log.is_connection_error && (
                              <span className="text-xs px-2 py-1 bg-red-500/20 text-red-300 rounded font-medium">
                                Connection Error
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Response Time Chart Toggle */}
                    <div className="mt-4">
                      <button
                        onClick={() => toggleEndpointChart(endpoint.id)}
                        className="text-sm text-[#3599B8] hover:text-[#2C9C7A] transition-colors font-medium"
                      >
                        {selectedEndpointLogs[endpoint.id] ? 'Hide' : 'Show'} Response Time (24h)
                      </button>
                    </div>

                    {/* Response Time Chart */}
                    {selectedEndpointLogs[endpoint.id] && (
                      <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                        <h4 className="text-sm font-semibold text-white mb-3">
                          Response Time - Last 24 Hours
                        </h4>
                        <ResponseTimeChart logs={selectedEndpointLogs[endpoint.id]} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Incident History */}
        {incidents.length > 0 && (
          <div className="mt-8 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6">
            <h2 className="text-xl font-bold text-white mb-4">Recent Incidents</h2>
            <div className="space-y-3">
              {incidents.map((incident) => {
                const duration = incident.resolved_at
                  ? differenceInMinutes(
                      new Date(incident.resolved_at),
                      new Date(incident.started_at)
                    )
                  : null;

                return (
                  <div
                    key={incident.id}
                    className="p-4 bg-white/5 rounded-lg border border-white/10"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              incident.is_resolved
                                ? 'bg-emerald-500/10 text-emerald-400'
                                : 'bg-red-500/10 text-red-400'
                            }`}
                          >
                            {incident.is_resolved ? 'Resolved' : 'Active'}
                          </span>
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold uppercase ${
                              incident.severity === 'critical'
                                ? 'bg-red-500/20 text-red-300'
                                : incident.severity === 'high'
                                ? 'bg-orange-500/20 text-orange-300'
                                : 'bg-yellow-500/20 text-yellow-300'
                            }`}
                          >
                            {incident.severity}
                          </span>
                          <span className="text-sm font-medium text-white">
                            {incident.endpoint?.label || 'Unknown Endpoint'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-300 mb-2">
                          {incident.description}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-400">
                          <span>
                            Started: {new Date(incident.started_at).toLocaleString()}
                          </span>
                          {incident.resolved_at && (
                            <>
                              <span>•</span>
                              <span>
                                Resolved: {new Date(incident.resolved_at).toLocaleString()}
                              </span>
                              <span>•</span>
                              <span>Duration: {duration} min</span>
                            </>
                          )}
                          {!incident.resolved_at && (
                            <>
                              <span>•</span>
                              <span>
                                Ongoing for{' '}
                                {formatDistanceToNow(new Date(incident.started_at))}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceDetail;
